import React, { useLayoutEffect, useRef, useState } from 'react';
import { cn } from 'src/lib/utils';

function generateDutyTitleFromDescription(description) {
  const desc = String(description || '').trim();
  if (!desc) return '';

  const prefixFromSeparator = [':', ' – ', ' - ']
    .map((sep) => {
      if (!desc.includes(sep)) return '';
      const prefix = desc.split(sep)[0].trim();
      return prefix.length >= 3 && prefix.length <= 50 ? prefix : '';
    })
    .find(Boolean);

  if (prefixFromSeparator) return prefixFromSeparator;
  return desc.split(/\s+/).slice(0, 4).join(' ');
}

/** Normalize API `duties: [{ title, description }]` for display. */
export function normalizeWorkExperienceDuties(exp) {
  const raw = exp?.duties;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((d) => d && typeof d === 'object')
    .map((d) => {
      const description = (
        d.description != null ? String(d.description) : ''
      ).trim();
      const titleFromDesc = generateDutyTitleFromDescription(description);
      return {
        title: (d.title != null ? String(d.title) : titleFromDesc).trim(),
        description,
      };
    })
    .filter((d) => d.title || d.description);
}

function dutyKey(duty) {
  return `${duty.title}::${duty.description}`;
}

function DutyItem({
  duty,
  expIndex,
  dutyIndex,
  isAccordionOpen,
  animationDelay,
}) {
  const descRef = useRef(null);
  const [descExpanded, setDescExpanded] = useState(false);
  const [showReadMore, setShowReadMore] = useState(false);
  const descId = `wxp-duty-desc-${expIndex}-${dutyIndex}`;
  const moreId = `wxp-duty-more-${expIndex}-${dutyIndex}`;

  useLayoutEffect(() => {
    if (!isAccordionOpen) {
      return undefined;
    }
    const el = descRef.current;
    if (!el) {
      return undefined;
    }
    const run = () => {
      if (descExpanded) {
        setShowReadMore(true);
        return;
      }
      setShowReadMore(el.scrollHeight > el.clientHeight + 2);
    };
    const id = requestAnimationFrame(run);
    return () => {
      cancelAnimationFrame(id);
    };
  }, [isAccordionOpen, duty.description, descExpanded]);

  return (
    <div className="duty-item" style={{ animationDelay: `${animationDelay}s` }}>
      <div className="duty-marker">
        <div className="duty-dot" />
        <div className="duty-line" />
      </div>
      <div className="duty-content">
        {duty.title ? (
          <div className="duty-item-title">{duty.title}</div>
        ) : null}
        <div
          ref={descRef}
          id={descId}
          className={cn('duty-item-desc', descExpanded && 'expanded')}
        >
          {duty.description}
        </div>
        {showReadMore ? (
          <button
            type="button"
            id={moreId}
            className="duty-read-more"
            style={{ display: 'inline' }}
            onClick={(e) => {
              e.stopPropagation();
              setDescExpanded((v) => !v);
            }}
          >
            {descExpanded ? 'Show less' : 'Show more'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Work experience list as expandable cards (matches work-experience-duties reference HTML).
 * Expanded body shows duties under the label "Responsibilities".
 */
export function WorkExperienceAccordionDisplay({
  workExperiences,
  openIndex,
  onOpenIndexChange,
  formatMonthFromNumeric,
}) {
  const [internalOpenIndex, setInternalOpenIndex] = useState(null);

  React.useEffect(() => {
    // Sync internal state from parent when provided.
    // This keeps the component usable even if parent state updates are disrupted.
    if (openIndex === undefined) return;
    setInternalOpenIndex(openIndex);
  }, [openIndex]);

  React.useEffect(() => {
    console.log('[WXP][accordion] openIndex changed', {
      openIndex,
      internalOpenIndex,
      len: Array.isArray(workExperiences) ? workExperiences.length : -1,
      hasSetter: typeof onOpenIndexChange === 'function',
    });
  }, [openIndex, internalOpenIndex, workExperiences, onOpenIndexChange]);

  return (
    <div className="wxp-exp-accordion">
      <div className="exp-list">
        {workExperiences.map((exp, index) => {
          const isPresent = exp.employment_period?.present;
          const duties = normalizeWorkExperienceDuties(exp);
          const isActive = internalOpenIndex === index;
          const company = exp.company || exp.company_name || '';
          const startLabel =
            `${formatMonthFromNumeric(exp.employment_period?.startMonth) || ''} ${exp.employment_period?.startYear || ''}`.trim();
          const endLabel = isPresent
            ? null
            : `${formatMonthFromNumeric(exp.employment_period?.endMonth) || ''} ${exp.employment_period?.endYear || ''}`.trim();

          const expKey = [
            exp.role,
            company,
            exp.employment_period?.startYear,
            exp.employment_period?.startMonth,
            exp.employment_period?.endYear,
            exp.employment_period?.endMonth,
            isPresent ? 'present' : '',
          ]
            .filter(Boolean)
            .join('|');

          return (
            <div
              key={expKey || `exp-fallback-${index}`}
              className={cn(
                'exp-card',
                isPresent ? 'exp-card--present' : 'exp-card--past',
                isActive && 'active'
              )}
              id={`wxp-exp-${index}`}
            >
              <div
                className="exp-head"
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  const next = isActive ? null : index;
                  console.log('[WXP][accordion] head clicked', {
                    index,
                    openIndex,
                    internalOpenIndex,
                    isActive,
                    next,
                  });
                  setInternalOpenIndex(next);
                  try {
                    onOpenIndexChange?.(next);
                  } catch (err) {
                    console.log(
                      '[WXP][accordion] onOpenIndexChange error',
                      err
                    );
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    const next = isActive ? null : index;
                    console.log('[WXP][accordion] head key toggle', {
                      index,
                      openIndex,
                      internalOpenIndex,
                      isActive,
                      next,
                      key: e.key,
                    });
                    setInternalOpenIndex(next);
                    try {
                      onOpenIndexChange?.(next);
                    } catch (err) {
                      console.log(
                        '[WXP][accordion] onOpenIndexChange error',
                        err
                      );
                    }
                  }
                }}
              >
                <div className="exp-summary">
                  <div className="exp-role">{exp.role}</div>
                  <div className="exp-company">{company}</div>
                  <div className="exp-period">
                    {startLabel}
                    {startLabel && (endLabel || isPresent) ? ' – ' : null}
                    {isPresent ? (
                      <span className="exp-period-badge">Present</span>
                    ) : (
                      endLabel
                    )}
                    {duties.length
                      ? ` · ${duties.length} ${duties.length === 1 ? 'responsibility' : 'responsibilities'}`
                      : ''}
                  </div>
                </div>
                <div className="exp-toggle" aria-hidden>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    width={16}
                    height={16}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
              </div>

              {!isActive && duties.length > 0 ? (
                <div className="exp-duty-chips">
                  {duties.slice(0, 2).map((d) => (
                    <span key={`chip-${dutyKey(d)}`} className="duty-chip">
                      <span className="duty-chip-dot" />
                      {d.title || 'Responsibility'}
                    </span>
                  ))}
                  {duties.length > 2 ? (
                    <span className="duty-chip duty-chip-more">
                      +{duties.length - 2} more
                    </span>
                  ) : null}
                </div>
              ) : null}

              <div className="exp-form">
                <div className="exp-form-inner">
                  <div className="exp-form-divider" />
                  <div className="duties-section">
                    <div className="duties-header">
                      <span className="duties-title">
                        Responsibilities{' '}
                        <span className="duties-count">{duties.length}</span>
                      </span>
                    </div>
                    {duties.map((duty, di) => (
                      <DutyItem
                        key={dutyKey(duty)}
                        duty={duty}
                        expIndex={index}
                        dutyIndex={di}
                        isAccordionOpen={isActive}
                        animationDelay={di * 0.05}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
